
import { useState, useEffect } from "react";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formSectionApi } from "@/services/api";
import { FormSection, FormSectionType, SectionType } from "@/models";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";

interface EditSectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSectionUpdated: () => Promise<void>;
  section: FormSection | null;
  isParentSubmitting: boolean;
}

const EditSectionDialog = ({
  isOpen,
  onClose,
  onSectionUpdated,
  section,
  isParentSubmitting = false
}: EditSectionDialogProps) => {
  const { toast } = useToast();
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionSubtitle, setSectionSubtitle] = useState("");
  const [sectionType, setSectionType] = useState<FormSectionType>("Default");
  const [multipleElements, setMultipleElements] = useState(false);
  const [minQuantity, setMinQuantity] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    if (section && isOpen) {
      setSectionTitle(section.section_title || "");
      setSectionSubtitle(section.section_subtitle || "");
      setSectionType(section.section_type);
      setMultipleElements(section.multiple_elements || false);
      
      // Properly handle the min_quantity value, including when it's 0
      const min = section.min_quantity !== undefined && section.min_quantity !== null 
        ? section.min_quantity 
        : null;
      setMinQuantity(min);
      
      // Log the min_quantity value to verify it's being correctly loaded
      console.log('Loading section data:', {
        title: section.section_title,
        minQuantity: section.min_quantity,
        minQuantityStateValue: min
      });
    }
  }, [section, isOpen]);
  
  const handleUpdateSection = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!section) return;
    
    try {
      setIsSubmitting(true);
      
      await formSectionApi.update(section.id, {
        section_title: sectionTitle,
        section_subtitle: sectionSubtitle,
        section_type: sectionType,
        multiple_elements: multipleElements,
        min_quantity: minQuantity
      });
      
      toast({
        title: "Section updated",
        description: "The section has been updated successfully",
      });
      
      await onSectionUpdated();
      onClose();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error updating section",
        description: error.message || "An error occurred while updating the section",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleClose = () => {
    if (!isSubmitting && !isParentSubmitting) {
      onClose();
    }
  };
  
  const stopEvent = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) handleClose();
    }}>
      <DialogContent className="sm:max-w-[425px]" onClick={stopEvent}>
        <DialogHeader>
          <DialogTitle>Edit Section</DialogTitle>
          <DialogDescription>
            Update the section details
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="section-title-edit" className="text-right col-span-1">
              Title
            </label>
            <Input
              id="section-title-edit"
              value={sectionTitle}
              onChange={(e) => setSectionTitle(e.target.value)}
              className="col-span-3"
              placeholder="Section title (optional)"
              disabled={isSubmitting || isParentSubmitting}
              onClick={stopEvent}
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="section-subtitle-edit" className="text-right col-span-1">
              Subtitle
            </label>
            <Input
              id="section-subtitle-edit"
              value={sectionSubtitle}
              onChange={(e) => setSectionSubtitle(e.target.value)}
              className="col-span-3"
              placeholder="Section subtitle (optional)"
              disabled={isSubmitting || isParentSubmitting}
              onClick={stopEvent}
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="section-type-edit" className="text-right col-span-1">
              Type
            </label>
            <div className="col-span-3" onClick={stopEvent}>
              <Select
                value={sectionType}
                onValueChange={(value) => setSectionType(value as SectionType)}
                disabled={isSubmitting || isParentSubmitting}
              >
                <SelectTrigger id="section-type-edit" onClick={stopEvent}>
                  <SelectValue placeholder="Select section type" />
                </SelectTrigger>
                <SelectContent position="popper" onClick={stopEvent} onCloseAutoFocus={(e) => e.preventDefault()}>
                  <SelectItem value="Default" onClick={stopEvent}>Default</SelectItem>
                  <SelectItem value="Applicants" onClick={stopEvent}>Applicants</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-1" />
            <div className="flex items-center space-x-2 col-span-3">
              <Checkbox
                id="multiple-elements-edit"
                checked={multipleElements}
                onCheckedChange={(checked) => setMultipleElements(checked === true)}
                disabled={isSubmitting || isParentSubmitting}
                onClick={stopEvent}
              />
              <label
                htmlFor="multiple-elements-edit"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                onClick={(e) => {
                  stopEvent(e);
                  if (!(isSubmitting || isParentSubmitting)) {
                    setMultipleElements(!multipleElements);
                  }
                }}
              >
                Allow multiple elements
              </label>
            </div>
          </div>
          
          {multipleElements && (
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="min-quantity-edit" className="text-right col-span-1">
                Min Quantity
              </label>
              <Input
                id="min-quantity-edit"
                type="number"
                min={0}
                value={minQuantity !== null ? minQuantity : ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : parseInt(e.target.value);
                  setMinQuantity(val);
                }}
                className="col-span-3"
                placeholder="Minimum elements required (optional)"
                disabled={isSubmitting || isParentSubmitting}
                onClick={stopEvent}
              />
            </div>
          )}
        </div>
        
        <DialogFooter onClick={stopEvent}>
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              stopEvent(e);
              handleClose();
            }}
            disabled={isSubmitting || isParentSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="button"
            onClick={handleUpdateSection}
            disabled={isSubmitting || isParentSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Update Section"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditSectionDialog;
